import { useState, useMemo } from 'react'
import { isValidPhoneNumber } from 'react-phone-number-input'
import PhoneInput from 'react-phone-number-input/input'
import { Api } from '../api'

type Phase = 'phone' | 'auth'
type PhoneStep = 'entry' | 'code'

const GoogleG = () => (
  <span className="flex items-center justify-center w-8 h-8 bg-white rounded-md shrink-0">
    <svg viewBox="0 0 24 24" className="w-4 h-4" xmlns="http://www.w3.org/2000/svg">
      <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
      <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
      <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" fill="#FBBC05"/>
      <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
    </svg>
  </span>
)

// Read the invite JWT's payload claims client-side. The signature is NOT
// verified — these claims drive UI hints only (pre-fill, etc.). The server
// re-validates the token on every endpoint that consumes it.
function decodeInvitePayload(token: string | null): { prefilled_phone: string | null } | null {
  if (!token) return null
  try {
    const payload = token.split('.')[1]
    return JSON.parse(atob(payload))
  } catch {
    return null
  }
}

interface Props {
  token: string | null
}

export default function AcceptInvitePage({ token }: Props) {
  const claims = useMemo(() => decodeInvitePayload(token), [token])
  const tokenInvalid = token === null || claims === null
  const prefilledPhone = claims?.prefilled_phone ?? null

  const [phase, setPhase] = useState<Phase>('phone')

  // Phase 'phone' state
  const [phone, setPhone]       = useState<string>(prefilledPhone ?? '')
  const [consent, setConsent]   = useState(false)
  const [phoneStep, setPhoneStep] = useState<PhoneStep>('entry')
  const [code, setCode]         = useState('')
  const [phoneError, setPhoneError]     = useState<string | null>(null)
  const [phoneLoading, setPhoneLoading] = useState(false)
  const [phoneVerified, setPhoneVerified] = useState(false)
  const phoneIsValid = isValidPhoneNumber(phone, 'US')

  // Phase 'auth' state
  const [showPassword, setShowPassword] = useState(false)
  const [password, setPassword]         = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [authError, setAuthError]       = useState<string | null>(null)
  const [authLoading, setAuthLoading]   = useState(false)
  const [googleLoading, setGoogleLoading] = useState(false)
  const [phoneOtpLoading, setPhoneOtpLoading] = useState(false)

  async function handleSendCode(e: React.FormEvent) {
    e.preventDefault()
    if (!token) return
    setPhoneError(null)
    setPhoneLoading(true)
    try {
      await Api.attachPhoneStart({ attachPhoneStartRequest: { token, phone } })
      setPhoneStep('code')
      setCode('')
    } catch {
      setPhoneError('Failed to send code. Check the phone number and try again.')
    } finally {
      setPhoneLoading(false)
    }
  }

  async function handleVerifyCode(e: React.FormEvent) {
    e.preventDefault()
    if (!token) return
    setPhoneError(null)
    setPhoneLoading(true)
    try {
      await Api.attachPhoneConfirm({
        attachPhoneConfirmRequest: { token, phone, code },
      })
      setPhoneVerified(true)
      setPhase('auth')
    } catch (err) {
      // 401 = bad code; 409 = phone already on another account; other = generic.
      const status = (err as { response?: { status?: number } })?.response?.status
      if (status === 409) {
        setPhoneError('This phone is already on another Kodiak account.')
      } else if (status === 401) {
        setPhoneError('Invalid or expired code.')
      } else {
        setPhoneError('Something went wrong. Please try again.')
      }
    } finally {
      setPhoneLoading(false)
    }
  }

  function handleSkipPhone() {
    setPhoneError(null)
    setPhase('auth')
  }

  async function handleSubmitPassword(e: React.FormEvent) {
    e.preventDefault()
    if (password !== confirmPassword) {
      setAuthError('Passwords do not match')
      return
    }
    if (!token) return
    setAuthError(null)
    setAuthLoading(true)
    try {
      await Api.acceptInvite({ acceptInviteRequest: { token, password } })
      window.location.href = '/'
    } catch {
      setAuthError('Failed to accept invite. The link may be invalid or expired.')
    } finally {
      setAuthLoading(false)
    }
  }

  async function handleGoogleAccept() {
    if (!token) return
    setAuthError(null)
    setGoogleLoading(true)
    try {
      const { url } = await Api.getGoogleLoginUrl({ inviteToken: token })
      window.location.href = url
    } catch {
      setAuthError('Failed to initiate Google sign-in.')
      setGoogleLoading(false)
    }
  }

  async function handlePhoneOtpAccept() {
    if (!token) return
    setAuthError(null)
    setPhoneOtpLoading(true)
    try {
      await Api.acceptInvite({ acceptInviteRequest: { token, password: null } })
      window.location.href = '/'
    } catch {
      setAuthError('Failed to accept invite.')
      setPhoneOtpLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-stone-50 flex items-center justify-center px-4">
      <div className="w-full max-w-sm">
        <div className="flex flex-col items-center mb-8">
          <img src="/bear_icon_transparent.svg" alt="" className="h-16 w-auto mb-4" />
          <h1 className="font-display font-black text-2xl tracking-widest text-bear uppercase leading-none">
            Kodiak Labs
          </h1>
          <p className="font-mono text-[10px] tracking-[0.25em] text-stone-400 uppercase mt-1">
            Accept your invite
          </p>
        </div>

        <div className="bg-white rounded-2xl border border-stone-200 p-6 space-y-4">
          {tokenInvalid && (
            <p className="font-mono text-[10px] text-coral text-center">
              This invite link is invalid or has expired.
            </p>
          )}

          {/* PHASE 1: phone (optional) */}
          {!tokenInvalid && phase === 'phone' && phoneStep === 'entry' && (
            <form onSubmit={handleSendCode} className="space-y-3">
              <p className="font-mono text-[10px] tracking-[0.15em] uppercase text-stone-500">
                Add a phone (optional)
              </p>
              <p className="font-mono text-[11px] leading-snug text-stone-500">
                Required for SMS updates and phone-based login.
              </p>
              <div>
                <label className="block font-mono text-[10px] tracking-[0.2em] uppercase text-stone-500 mb-1.5">
                  Phone number
                </label>
                <PhoneInput
                  country="US"
                  value={phone || undefined}
                  onChange={v => setPhone(v || '')}
                  className="w-full bg-stone-50 border border-stone-200 rounded-lg px-3 py-2.5
                    font-mono text-sm text-stone-900 placeholder-stone-400
                    focus:outline-none focus:border-bear/60 transition-colors"
                  placeholder="(415) 555-0100"
                  autoComplete="tel"
                />
              </div>
              <label className="flex items-start gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={consent}
                  onChange={e => setConsent(e.target.checked)}
                  className="mt-0.5 w-4 h-4 accent-bear cursor-pointer"
                />
                <span className="font-mono text-[11px] leading-snug text-stone-500">
                  I agree to receive SMS messages from Kodiak Labs at this
                  number for account notifications, event alerts (such as new
                  catch data or net status), and customer support. Message
                  frequency varies. Message and data rates may apply. Reply
                  STOP to opt out, HELP for help. See our{' '}
                  <a
                    href="/legal#sms-program"
                    target="_blank"
                    rel="noopener"
                    className="underline hover:text-stone-700"
                  >
                    SMS Policy &amp; Privacy
                  </a>.
                </span>
              </label>
              {phoneError && (
                <p className="font-mono text-[10px] text-coral">{phoneError}</p>
              )}
              <button
                type="submit"
                disabled={phoneLoading || !phoneIsValid || !consent}
                className="w-full px-4 py-2.5 rounded-xl bg-bear text-white
                  font-mono text-[11px] tracking-[0.15em] uppercase font-semibold
                  hover:bg-bear-light transition-colors cursor-pointer
                  disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {phoneLoading ? 'Sending…' : 'Send code'}
              </button>
              <button
                type="button"
                onClick={handleSkipPhone}
                className="w-full font-mono text-[10px] tracking-[0.15em] uppercase text-stone-400 hover:text-stone-700 transition-colors"
              >
                Skip — I'll use Google or password
              </button>
            </form>
          )}

          {!tokenInvalid && phase === 'phone' && phoneStep === 'code' && (
            <form onSubmit={handleVerifyCode} className="space-y-3">
              <p className="font-mono text-[10px] tracking-[0.15em] uppercase text-stone-500">
                Verify your phone
              </p>
              <p className="font-mono text-[11px] text-stone-500">
                Code sent to {phone}
              </p>
              <div>
                <label className="block font-mono text-[10px] tracking-[0.2em] uppercase text-stone-500 mb-1.5">
                  6-digit code
                </label>
                <input
                  type="text"
                  inputMode="numeric"
                  value={code}
                  onChange={e => setCode(e.target.value)}
                  className="w-full bg-stone-50 border border-stone-200 rounded-lg px-3 py-2.5
                    font-mono text-sm text-stone-900 placeholder-stone-400
                    focus:outline-none focus:border-bear/60 transition-colors"
                  placeholder="123456"
                  autoComplete="one-time-code"
                  required
                />
              </div>
              {phoneError && (
                <p className="font-mono text-[10px] text-coral">{phoneError}</p>
              )}
              <button
                type="submit"
                disabled={phoneLoading}
                className="w-full px-4 py-2.5 rounded-xl bg-bear text-white
                  font-mono text-[11px] tracking-[0.15em] uppercase font-semibold
                  hover:bg-bear-light transition-colors cursor-pointer
                  disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {phoneLoading ? 'Verifying…' : 'Verify'}
              </button>
              <button
                type="button"
                onClick={() => { setPhoneStep('entry'); setPhoneError(null) }}
                className="w-full font-mono text-[10px] tracking-[0.15em] uppercase text-stone-400 hover:text-stone-700 transition-colors"
              >
                Back
              </button>
            </form>
          )}

          {/* PHASE 2: auth */}
          {!tokenInvalid && phase === 'auth' && (
            <>
              <button
                type="button"
                onClick={handleGoogleAccept}
                disabled={googleLoading}
                className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl
                  bg-bear hover:bg-bear-light active:bg-bear
                  transition-colors cursor-pointer
                  disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <GoogleG />
                <span className="flex-1 text-center font-mono text-[11px] tracking-[0.15em] uppercase font-semibold text-white pr-8">
                  {googleLoading ? 'Redirecting…' : 'Continue with Google'}
                </span>
              </button>

              {phoneVerified && (
                <button
                  type="button"
                  onClick={handlePhoneOtpAccept}
                  disabled={phoneOtpLoading}
                  className="w-full px-4 py-2.5 rounded-xl border border-bear text-bear
                    font-mono text-[11px] tracking-[0.15em] uppercase font-semibold
                    hover:bg-bear/10 transition-colors cursor-pointer
                    disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {phoneOtpLoading ? 'Setting up…' : 'Use phone for sign-in'}
                </button>
              )}

              <button
                type="button"
                onClick={() => { setShowPassword(v => !v); setAuthError(null) }}
                className="w-full px-4 py-2.5 rounded-xl border border-stone-200
                  bg-white hover:bg-stone-50
                  font-mono text-[11px] tracking-[0.15em] uppercase font-semibold
                  text-stone-700 hover:text-stone-900
                  transition-colors cursor-pointer"
              >
                {showPassword ? 'Hide password' : 'Set a password'}
              </button>

              {showPassword && (
                <form onSubmit={handleSubmitPassword} className="space-y-3">
                  <div>
                    <label className="block font-mono text-[10px] tracking-[0.2em] uppercase text-stone-500 mb-1.5">
                      Password
                    </label>
                    <input
                      type="password"
                      value={password}
                      onChange={e => setPassword(e.target.value)}
                      className="w-full bg-stone-50 border border-stone-200 rounded-lg px-3 py-2.5
                        font-mono text-sm text-stone-900 placeholder-stone-400
                        focus:outline-none focus:border-bear/60 transition-colors"
                      placeholder="••••••••"
                      autoComplete="new-password"
                      required
                      minLength={8}
                    />
                  </div>
                  <div>
                    <label className="block font-mono text-[10px] tracking-[0.2em] uppercase text-stone-500 mb-1.5">
                      Confirm Password
                    </label>
                    <input
                      type="password"
                      value={confirmPassword}
                      onChange={e => setConfirmPassword(e.target.value)}
                      className="w-full bg-stone-50 border border-stone-200 rounded-lg px-3 py-2.5
                        font-mono text-sm text-stone-900 placeholder-stone-400
                        focus:outline-none focus:border-bear/60 transition-colors"
                      placeholder="••••••••"
                      autoComplete="new-password"
                      required
                      minLength={8}
                    />
                  </div>
                  {authError && (
                    <p className="font-mono text-[10px] text-coral">{authError}</p>
                  )}
                  <button
                    type="submit"
                    disabled={authLoading}
                    className="w-full px-4 py-2.5 rounded-xl bg-bear text-white
                      font-mono text-[11px] tracking-[0.15em] uppercase font-semibold
                      hover:bg-bear-light transition-colors cursor-pointer
                      disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {authLoading ? 'Setting up account…' : 'Set password & join'}
                  </button>
                </form>
              )}

              {authError && !showPassword && (
                <p className="font-mono text-[10px] text-coral">{authError}</p>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  )
}
